import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nProvider";
import {
  X, Check, Share2, Copy, MessageCircle, ShieldAlert,
  Loader2, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function ConversationShareModal({ messages, conversationId, module, onClose }) {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const [selected, setSelected] = useState(new Set());
  const [step, setStep] = useState("select"); // select | confirm | done
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const name = user?.persona?.preferred_name || user?.username || "You";

  const toggleMessage = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === messages.length) setSelected(new Set());
    else setSelected(new Set(messages.map((_, i) => i)));
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const { data } = await axios.post(`${API}/conversations/share`, {
        conversation_id: conversationId,
        selected_indices: [...selected].sort(),
      }, { headers });
      setShareResult(data);
      setStep("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to share");
    } finally {
      setSharing(false);
    }
  };

  const shareUrl = shareResult
    ? `${window.location.origin}/api/share/conversation/${shareResult.id}/card`
    : "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied!");
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(
      `Check out my conversation with Omni AI:\n${shareUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareResult?.title || "Omni Conversation",
          text: "Check out my conversation with Omni AI",
          url: shareUrl,
        });
      } catch (e) { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[85vh] flex flex-col animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="card-omni bg-card overflow-hidden flex flex-col max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-border/50 flex-shrink-0">
            <div className="flex items-center gap-3">
              <Share2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-lg font-medium">
                {step === "select" ? "Select Messages" : step === "confirm" ? "Confirm Share" : "Shared!"}
              </h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-muted/50">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Step: Select Messages */}
          {step === "select" && (
            <>
              <div className="px-5 py-3 flex items-center justify-between border-b border-border/30 flex-shrink-0">
                <p className="text-sm text-muted-foreground">
                  {selected.size} of {messages.length} selected
                </p>
                <button
                  data-testid="select-all-btn"
                  onClick={selectAll}
                  className="text-xs text-primary hover:underline"
                >
                  {selected.size === messages.length ? "Deselect all" : "Select all"}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.map((msg, idx) => {
                  const isUser = msg.role === "user";
                  const isSelected = selected.has(idx);
                  return (
                    <button
                      key={idx}
                      data-testid={`share-msg-${idx}`}
                      onClick={() => toggleMessage(idx)}
                      className={cn(
                        "w-full text-left p-3 rounded-2xl border-2 transition-all",
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-transparent hover:bg-muted/30"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-5 h-5 rounded-md border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-all",
                          isSelected ? "bg-primary border-primary" : "border-muted-foreground/30"
                        )}>
                          {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn("text-xs font-medium mb-1", isUser ? "text-primary" : "text-blue-600")}>
                            {isUser ? name : "Omni"}
                          </p>
                          <p className="text-sm text-foreground/80 line-clamp-3 leading-relaxed">
                            {msg.content}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-5 border-t border-border/50 flex-shrink-0">
                <Button
                  data-testid="continue-share-btn"
                  onClick={() => setStep("confirm")}
                  disabled={selected.size === 0}
                  className="w-full btn-omni bg-primary text-primary-foreground gap-2"
                >
                  Continue with {selected.size} message{selected.size !== 1 ? "s" : ""}
                </Button>
              </div>
            </>
          )}

          {/* Step: Privacy Warning */}
          {step === "confirm" && (
            <div className="p-6 space-y-6">
              <div className="p-5 bg-amber-50 dark:bg-amber-950/20 rounded-2xl border border-amber-200/50">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-400 mb-1">Privacy Notice</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300/80 leading-relaxed">
                      The {selected.size} message{selected.size !== 1 ? "s" : ""} you selected will become
                      <strong> publicly viewable</strong> by anyone with the link. This cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="rounded-2xl border border-border/50 overflow-hidden">
                <div className="px-4 py-3 bg-muted/30 border-b border-border/30">
                  <p className="text-xs text-muted-foreground font-medium">Preview ({selected.size} messages)</p>
                </div>
                <div className="p-4 max-h-48 overflow-y-auto space-y-2">
                  {[...selected].sort().slice(0, 4).map(idx => {
                    const msg = messages[idx];
                    return (
                      <div key={idx} className={cn(
                        "p-2.5 rounded-xl text-sm",
                        msg.role === "user" ? "bg-primary/5" : "bg-blue-50 dark:bg-blue-950/20"
                      )}>
                        <span className={cn(
                          "text-[10px] font-semibold",
                          msg.role === "user" ? "text-primary" : "text-blue-600"
                        )}>
                          {msg.role === "user" ? name : "Omni"}
                        </span>
                        <p className="text-xs text-foreground/70 line-clamp-2 mt-0.5">{msg.content}</p>
                      </div>
                    );
                  })}
                  {selected.size > 4 && (
                    <p className="text-xs text-muted-foreground text-center">
                      + {selected.size - 4} more messages
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setStep("select")}
                  className="flex-1 rounded-full"
                >
                  Back
                </Button>
                <Button
                  data-testid="confirm-share-btn"
                  onClick={handleShare}
                  disabled={sharing}
                  className="flex-1 btn-omni bg-primary text-primary-foreground gap-2"
                >
                  {sharing ? <><Loader2 className="w-4 h-4 animate-spin" /> Sharing...</> : "Share Conversation"}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Done */}
          {step === "done" && shareResult && (
            <div className="p-6 space-y-6">
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="font-heading text-xl font-medium mb-1">Conversation Shared</h3>
                <p className="text-sm text-muted-foreground">
                  {shareResult.message_count} messages are now publicly viewable
                </p>
              </div>

              {/* Share link */}
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-2xl">
                <input
                  data-testid="share-url-input"
                  className="flex-1 text-sm bg-transparent outline-none text-foreground truncate"
                  value={shareUrl}
                  readOnly
                  onClick={e => e.target.select()}
                />
                <button
                  data-testid="copy-share-link-btn"
                  onClick={copyLink}
                  className="p-2 rounded-xl hover:bg-muted text-muted-foreground"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>

              {/* Share buttons */}
              <div className="grid grid-cols-3 gap-3">
                <button
                  data-testid="share-whatsapp-btn"
                  onClick={shareWhatsApp}
                  className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-green-50 dark:bg-green-950/20 hover:bg-green-100 transition-all"
                >
                  <MessageCircle className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                  <span className="text-xs font-medium text-green-700">WhatsApp</span>
                </button>
                <button
                  data-testid="share-native-btn"
                  onClick={shareNative}
                  className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-primary/5 hover:bg-primary/10 transition-all"
                >
                  <Share2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  <span className="text-xs font-medium text-primary">Share</span>
                </button>
                <a
                  data-testid="share-view-btn"
                  href={`/share/conversation/${shareResult.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-muted/50 hover:bg-muted transition-all"
                >
                  <ExternalLink className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                  <span className="text-xs font-medium">View</span>
                </a>
              </div>

              <Button
                onClick={onClose}
                variant="ghost"
                className="w-full rounded-full text-muted-foreground"
              >
                Done
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
